import CommentThread from '../../../shared/components/comments/CommentThread';
import { useLinkedEntityCards } from '../../../shared/components/linked-entities/LinkedEntityCardHost';
import CreateEventTypeDialog from '../components/CreateEventTypeDialog';
import { PageNotice } from '../../../shared/components/PageFeedback';
import useRegisteredModuleFilters from '../../moduleFilters';
import { calendarFormSchema } from '../config/calendarForm.schema';
import useFormTableFields from '../../../shared/data/useFormTableFields';
import { listCalendarCustomFields } from '../services/calendar-custom-fields.service';
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from '../../../shared/assets/AppIcons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import { CalendarDateGrid, CreateEventDialog, EventDetailModal } from '@/modules/calendar/components';
import { useCalendarData } from '@/modules/calendar/hooks';
import {
  buildCalendarColumns,
  buildCalendarMetrics,
  buildCalendarRowActions,
  calendarFilterConfig,
  initialCalendarFilters,
  resetCalendarFilters,
} from '../config/calendarPage.config';
import {
  buildCalendarMonthDays,
  filterCalendarEvents,
  formatCalendarMonthLabel,
  getDefaultUpcomingEvents,
  hasActiveEventFilters,
  isEventCompleted,
  normalizeEventType,
  sortEventsByDateTime,
} from '../utils';
import {
  AdaptiveFilterBar,
  DataTable,
  MetricGrid,
  PageActionRow,
  PageDivider,
  PageFrame,
  PageHeader,
  PrimaryActionButton,
  ConfirmActionDialog,
} from '../../../shared/components/patterns';
import { AssetIcon } from '../../../shared/assets';
import '../styles/calendar-page.css';

function groupEventsByDate(events = []) {
  return events.reduce((accumulator, event) => {
    if (!event.date) return accumulator;
    return {
      ...accumulator,
      [event.date]: [...(accumulator[event.date] || []), event],
    };
  }, {});
}

function isCurrentMonthEvent(event) {
  if (!event?.date) return false;
  const date = new Date(`${event.date}T00:00:00`);
  const today = new Date();
  return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
}

function filterCalendarEventsByOverview(events = [], overviewFilter) {
  if (!overviewFilter) return events;
  if (overviewFilter === 'meeting') {
    return events.filter((event) => normalizeEventType(event.type) === 'meeting');
  }
  if (overviewFilter === 'meetings') {
    return events.filter((event) => ['meeting', 'internal'].includes(normalizeEventType(event.type)));
  }
  if (overviewFilter === 'completed') {
    return events.filter(isEventCompleted);
  }
  if (overviewFilter === 'month') {
    return events.filter(isCurrentMonthEvent);
  }
  return events;
}

export default function CalendarPage() {
  const tableFields = useFormTableFields(calendarFormSchema.id, listCalendarCustomFields);
  const navigate = useNavigate();
  const { openEntityCard } = useLinkedEntityCards();
  const { capabilities } = useSession();
  const [searchParams] = useSearchParams();
  const {
    events,
    eventTypes,
    staff,
    clients,
    suppliers,
    currentUser,
    isLoadingEvents,
    eventsError,
    calendarCatalogStatus,
    date: selectedDate,
    selectedDateRange,
    selectedEvent,
    eventNotesById,
    eventNotesError,
    formErrors,
    isSubmitting,
    setSelectedEvent,
    isCreateDialogOpen,
    isTypeDialogOpen,
    selectEventType,
    backToEventTypes,
    eventDialogMode,
    newEvent,
    handleDateSelect,
    clearSelection,
    toggleAttendee,
    updateNewEventField,
    openCreateDialog,
    openEditEvent,
    handleSubmitEvent,
    deleteEvent,
    loadEventNotes,
    addEventNote,
    deleteEventNote,
    closeEventDialog,
  } = useCalendarData();
  const [filters, setFilters] = useState(() => {
    const rangeParam = searchParams.get('range');
    const normalizedRange = rangeParam === 'next7' ? 'next7days' : rangeParam;
    return {
      ...initialCalendarFilters,
      range: normalizedRange === 'next7days' ? 'next7days' : initialCalendarFilters.range,
    };
  });
  const [activeOverviewFilter, setActiveOverviewFilter] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [focusComments, setFocusComments] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [showCalendar, setShowCalendar] = useState(true);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const calendarModuleDisabled = calendarCatalogStatus === 'module_disabled';
  const calendarAuthRequired = calendarCatalogStatus === 'auth_required';
  const calendarTenantDatabaseUnavailable = calendarCatalogStatus === 'tenant_database_unavailable';
  const calendarRuntimeBlocked =
    calendarModuleDisabled || calendarAuthRequired || calendarTenantDatabaseUnavailable;

  const calendarDays = useMemo(() => buildCalendarMonthDays(visibleMonth), [visibleMonth]);
  const visibleMonthLabel = useMemo(() => formatCalendarMonthLabel(visibleMonth), [visibleMonth]);
  const dynamicFilterConfig = useMemo(
    () => ({
      ...calendarFilterConfig,
      groups: [
        ...calendarFilterConfig.groups,
        {
          key: 'staffId',
          label: 'Personale',
          selection: 'single',
          emptyValue: '',
          options: staff.map((member) => ({ value: String(member.id), label: member.name })),
        },
        {
          key: 'clientId',
          label: 'Cliente',
          selection: 'single',
          emptyValue: '',
          options: clients.map((client) => ({
            value: String(client.id),
            label: client.company || client.businessName || client.name,
          })),
        },
        {
          key: 'supplierId',
          label: 'Fornitore',
          selection: 'single',
          emptyValue: '',
          options: suppliers.map((supplier) => ({
            value: String(supplier.id),
            label: supplier.displayName || supplier.legalName,
          })),
        },
      ],
    }),
    [clients, staff, suppliers]
  );
  const moduleFilters = useRegisteredModuleFilters('calendar', events, { clientId: clients, supplierId: suppliers });
  const moduleEvents = moduleFilters.filterRows(events);
  const filteredForGrid = useMemo(() => {
    const filtered = filterCalendarEvents(moduleEvents, { ...filters, selectedDateRange }, '');
    return sortEventsByDateTime(filterCalendarEventsByOverview(filtered, activeOverviewFilter));
  }, [activeOverviewFilter, moduleEvents, filters, selectedDateRange]);
  const tableEvents = useMemo(() => {
    const filtered = sortEventsByDateTime(
      filterCalendarEventsByOverview(
        filterCalendarEvents(moduleEvents, { ...filters, selectedDateRange }, selectedDate),
        activeOverviewFilter
      )
    );
    const hasFilters =
      moduleFilters.hasActiveFilters || activeOverviewFilter || selectedDate || hasActiveEventFilters(filters, selectedDateRange);
    return hasFilters ? filtered : getDefaultUpcomingEvents(filtered, 15);
  }, [activeOverviewFilter, moduleEvents, filters, selectedDate, selectedDateRange]);
  const eventsByDate = useMemo(() => groupEventsByDate(filteredForGrid), [filteredForGrid]);
  const metrics = useMemo(() => buildCalendarMetrics(events), [events]);
  const columns = useMemo(() => buildCalendarColumns({ onOpenEvent: openEventDetail }), []);

  useEffect(() => {
    if ((detailOpen || (isCreateDialogOpen && eventDialogMode === 'edit')) && selectedEvent && !selectedEvent.isReadOnly) {
      loadEventNotes(selectedEvent.id);
    }
  }, [detailOpen, selectedEvent, isCreateDialogOpen, eventDialogMode]);

  function updateFilter(key, value) {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function resetAllFilters() {
    setFilters(resetCalendarFilters());
    setActiveOverviewFilter(null);
    clearSelection();
  }

  function toggleOverviewFilter(metricId) {
    setActiveOverviewFilter((current) => (current === metricId ? null : metricId));
    clearSelection();
  }

  function showNext15Days() {
    setFilters((current) => ({
      ...current,
      range: current.range === 'next15' ? '' : 'next15',
    }));
    clearSelection();
  }

  function goToPreviousMonth() {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1));
  }

  function goToNextMonth() {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1));
  }

  function openEventDetail(event) {
    setFocusComments(false);
    setSelectedEvent(event);
    setDetailOpen(true);
  }

  function closeEventDetail() {
    setDetailOpen(false);
    setSelectedEvent(null);
  }

  function editEvent(event) {
    openEditEvent(event);
  }

  function commentEvent(event) {
    setSelectedEvent(event);
    setFocusComments(true);
    setDetailOpen(true);
  }

  function sourceAction(event, mode) {
    if (mode === 'comments' && event?.linkedEntity && openEntityCard({
      entityType: event.linkedEntity.type, entityId: event.linkedEntity.id, mode,
    })) return;
    if (!event?.linkedEntity?.route) return;
    const url = new URL(event.linkedEntity.route, window.location.origin);
    url.searchParams.set('entityType', event.linkedEntity.type);
    url.searchParams.set('entityId', String(event.linkedEntity.id));
    url.searchParams.set('mode', mode);
    navigate(`${url.pathname}?${url.searchParams.toString()}`);
  }

  async function removeEvent(event) {
    const result = await deleteEvent(event);
    if (result?.ok) {
      setDetailOpen(false);
    }
  }

  const rowActions = useMemo(
    () =>
      buildCalendarRowActions({
        onDetail: openEventDetail,
        onEdit: editEvent,
        onComment: commentEvent,
        onDelete: setDeleteRequest,
        onSourceAction: sourceAction,
        canDelete: capabilities?.canManageAccountingDestructiveActions === true,
      }),
    [capabilities]
  );

  return (
    <PageFrame filterModel={moduleFilters} className="calendar-page">
      {!calendarRuntimeBlocked ? (
        <PageHeader title="Calendario" description="Gestione centralizzata degli eventi aziendali" />
      ) : null}

      {calendarRuntimeBlocked ? (
        <PageNotice>
          {eventsError ||
            (calendarModuleDisabled
              ? 'Modulo calendario non abilitato per questo workspace.'
              : calendarAuthRequired
                ? 'Sessione non autorizzata per il backend calendario.'
                : 'Database tenant non disponibile per il modulo calendario.')}
        </PageNotice>
      ) : null}
      {!calendarRuntimeBlocked && eventsError ? <PageNotice>{eventsError}</PageNotice> : null}

      {!calendarRuntimeBlocked ? (
        <>
          <MetricGrid
            metrics={metrics.map((metric) => ({
              ...metric,
              active: activeOverviewFilter === metric.id,
              onClick: () => toggleOverviewFilter(metric.id),
            }))}
          />

          <PageDivider />
          <AdaptiveFilterBar
            config={dynamicFilterConfig}
            values={filters}
            onChange={updateFilter}
            onReset={resetAllFilters}
            filtersLabel="Apri filtri calendario"
            resetLabel="Reset filtri calendario"
          />
          <PageDivider />
          <PageActionRow>
            <PrimaryActionButton onClick={openCreateDialog} iconKey="actions.add">
              Crea
            </PrimaryActionButton>
          </PageActionRow>

          <section className="calendar-section">
            <div className="calendar-section__header">
              <div>
                <h2>Seleziona una data</h2>
                <p>Scegli un giorno per focalizzare la tabella eventi.</p>
              </div>
              <div className="calendar-section__actions">
                <button
                  type="button"
                  className="calendar-range-button"
                  onClick={() => setShowCalendar((current) => !current)}
                  aria-expanded={showCalendar}
                >
                  {showCalendar ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {showCalendar ? 'Nascondi calendario' : 'Mostra calendario'}
                </button>
                <button
                  type="button"
                  className={
                    filters.range === 'next15' ? 'calendar-range-button is-active' : 'calendar-range-button'
                  }
                  onClick={showNext15Days}
                  aria-pressed={filters.range === 'next15'}
                >
                  Prossimi 15 giorni
                </button>
                <button
                  type="button"
                  className="calendar-reset-button"
                  onClick={clearSelection}
                  aria-label="Reset data selezionata"
                  title="Reset data selezionata"
                >
                  <AssetIcon name="actions.reset" size={32} />
                </button>
              </div>
            </div>

            {showCalendar ? (
              <CalendarDateGrid
                days={calendarDays}
                eventsByDate={eventsByDate}
                selectedDate={selectedDate}
                selectedDateRange={selectedDateRange}
                monthLabel={visibleMonthLabel}
                onPreviousMonth={goToPreviousMonth}
                onNextMonth={goToNextMonth}
                onSelectDate={handleDateSelect}
                onOpenEvent={openEventDetail}
              />
            ) : null}
          </section>

          <section className="calendar-section">
            <div className="calendar-section__header">
              <div>
                <h2>Tutti gli eventi</h2>
                <p>
                  {selectedDate || selectedDateRange.startDate
                    ? 'Eventi filtrati sulla selezione calendario.'
                    : hasActiveEventFilters(filters, selectedDateRange) || activeOverviewFilter
                      ? 'Risultati filtrati.'
                      : 'Prossimi 15 eventi in ordine cronologico.'}
                </p>
              </div>
            </div>

            <DataTable
              ariaLabel="Eventi calendario"
              formSchema={calendarFormSchema}
              customFields={tableFields.definitions}
              error={tableFields.error}
              columns={columns}
              rows={tableEvents}
              getRowId={(event) => event.id}
              loading={isLoadingEvents}
              emptyState="Nessun evento trovato con i filtri attuali."
              rowActions={rowActions}
              onRowClick={openEventDetail}
            />
          </section>

          <EventDetailModal
            event={selectedEvent}
            isOpen={detailOpen && !isCreateDialogOpen}
            onClose={closeEventDetail}
            onEdit={editEvent}
            onDelete={removeEvent}
            focusComments={focusComments}
            comments={eventNotesById[selectedEvent?.id] || []}
            commentsError={eventNotesError}
            currentUser={currentUser}
            staff={staff}
            clients={clients}
            suppliers={suppliers}
            enabledModuleKeys={capabilities?.enabledModuleKeys}
            onAddNote={(payload) => addEventNote(selectedEvent?.id, payload)}
            onDeleteNote={(noteId) => deleteEventNote(selectedEvent?.id, noteId)}
          />
          <ConfirmActionDialog
            open={Boolean(deleteRequest)}
            title="Eliminare questo evento?"
            description="L’evento e i relativi commenti verranno eliminati definitivamente."
            confirmLabel="Elimina"
            cancelLabel="Annulla"
            tone="danger"
            onCancel={() => setDeleteRequest(null)}
            onConfirm={async () => {
              await removeEvent(deleteRequest);
              setDeleteRequest(null);
            }}
          />

          <CreateEventTypeDialog
            open={isTypeDialogOpen}
            eventTypes={eventTypes}
            onOpenChange={(open) => { if (!open) closeEventDialog(); }}
            onSelectType={selectEventType}
          />
          <CreateEventDialog
            open={isCreateDialogOpen}
            onOpenChange={(open) => {
              if (!open) closeEventDialog();
            }}
            mode={eventDialogMode}
            comments={isCreateDialogOpen && selectedEvent && !selectedEvent.isReadOnly ? <CommentThread key={selectedEvent.id}
              title="Commenti" comments={eventNotesById[selectedEvent.id] || []} currentUser={currentUser} staff={staff}
              error={eventNotesError} placeholder="Scrivi un commento sull’evento..." submitLabel="Aggiungi commento"
              onSubmitComment={payload => addEventNote(selectedEvent.id, payload)}
              onSubmitReply={(parentId, payload) => addEventNote(selectedEvent.id, { ...payload, parentId })}
              onDeleteComment={comment => deleteEventNote(selectedEvent.id, comment.id)} allowReplies /> : null}
            onBack={backToEventTypes}
            newEvent={newEvent}
            eventTypes={eventTypes}
            staff={staff}
            clients={clients}
            suppliers={suppliers}
            formErrors={formErrors}
            isSubmitting={isSubmitting}
            onFieldChange={updateNewEventField}
            onToggleAttendee={toggleAttendee}
            onSubmit={handleSubmitEvent}
          />
        </>
      ) : null}
    </PageFrame>
  );
}
